import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Tooltip,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
} from "@mui/material";

import { Editor } from "@tinymce/tinymce-react";

import axios from "axios";

import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";

const API = String(process.env.REACT_APP_API_URL || "")
  .trim()
  .replace(/\/+$/, "");

export default function AccountManager({ onAccountsChange }) {
  const [accounts, setAccounts] = useState([]);
  const [openEdit, setOpenEdit] = useState(false);
  const [openImap, setOpenImap] = useState(false);
  const [imapBusy, setImapBusy] = useState(false);
  const [imapForm, setImapForm] = useState({
    email: "",
    password: "",
    imapUsername: "",
    imapHost: "mail.dlx.dk",
    imapPort: "143",
    imapSecure: false,
    smtpHost: "mail.dlx.dk",
    smtpPort: "587",
    smtpSecure: false,
  });
  const [editingAccount, setEditingAccount] = useState(null);
  const [signatureHtml, setSignatureHtml] = useState("");
  const [replySignatureHtml, setReplySignatureHtml] = useState("");

  // Mostrar las firma logo
  const [logoPath, setLogoPath] = useState(null);

  const [imapNotice, setImapNotice] = useState({
    open: false,
    title: "",
    message: "",
    severity: "success",
  });

  const closeImapNotice = () =>
    setImapNotice((s) => ({ ...s, open: false }));

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    if (!API) {
      console.error("Falta REACT_APP_API_URL en .env de unibox-mail");
      return;
    }
    const res = await axios.get(`${API}/auth/accounts`);
    setAccounts(res.data);
  };

  const refreshAccountsAndNotifyParent = async () => {
    await loadAccounts();
    onAccountsChange?.();
  };

  function stringToColor(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const c = (hash & 0x00ffffff).toString(16).toUpperCase();
    return "#" + "000000".substring(0, 6 - c.length) + c;
  }

  const openGoogleOAuth = async () => {
    try {
      const res = await axios.post(`${API}/auth/google/start`);
      const url = res.data?.url;
      if (!url || typeof url !== "string") {
        alert("No se pudo iniciar Google OAuth.");
        return;
      }
      if (window.top) {
        window.top.location.href = url;
      } else {
        window.location.href = url;
      }
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || "Error al iniciar conexión con Google.");
    }
  };

  const openEditDialog = async (acc) => {
    setEditingAccount(acc);

    try {
      const res = await axios.get(`${API}/signatures/${acc.email}`);
      setSignatureHtml(res.data.signature_html || "");
      setReplySignatureHtml(res.data.reply_signature_html || "");
    } catch (err) {
      if (err.response?.status === 404) {
        // crear fila vacía automáticamente
        await axios.put(`${API}/signatures/${acc.email}`, {
          signature_html: "",
          reply_signature_html: "",
        });

        setSignatureHtml("");
        setReplySignatureHtml("");
        setLogoPath(null); // <-- mejor poner null
      }
    }
    setOpenEdit(true);
  };

  const saveSignatures = async () => {
    await axios.put(`${API}/signatures/${editingAccount.email}`, {
      signature_html: signatureHtml,
      reply_signature_html: replySignatureHtml,
    });

    setOpenEdit(false);
    await refreshAccountsAndNotifyParent();
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const form = new FormData();
    form.append("file", file);

    try {
      const res = await axios.post(
        `${API}/signatures/upload-logo/${editingAccount.email}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      alert("Logo subido correctamente ✔️");

      if (res.data?.logo_path) {
        const fullUrl = `${API}${res.data.logo_path}`;
        setLogoPath(fullUrl);

        // Insertar la imagen real en TinyMCE
        if (!signatureHtml.includes(fullUrl)) {
          setSignatureHtml(
            signatureHtml +
              `<br/><img src="${fullUrl}" style="max-width:180px;" />`
          );
        }
      }
    } catch (err) {
      console.error(err);
      alert("Error al subir logo");
    }
  };

  const deleteAccount = async (id) => {
    if (!window.confirm("¿Eliminar esta cuenta?")) return;
    await axios.delete(`${API}/auth/account/${id}`);
    await refreshAccountsAndNotifyParent();
  };

  const submitImap = async () => {
    if (!API) {
      setImapNotice({
        open: true,
        title: "Configuración",
        message:
          "Falta REACT_APP_API_URL en el .env de unibox-mail.\n\nEjemplo:\nREACT_APP_API_URL=http://localhost:4000\n\n(Reinicia npm start tras guardar.)",
        severity: "warning",
      });
      return;
    }
    if (!imapForm.email.trim() || !imapForm.password || !imapForm.imapHost.trim()) {
      setImapNotice({
        open: true,
        title: "Faltan datos",
        message: "Indica email, contraseña y servidor IMAP.",
        severity: "warning",
      });
      return;
    }
    setImapBusy(true);
    const imapUrl = `${API}/auth/imap/connect`;
    try {
      await axios.post(imapUrl, {
        email: imapForm.email.trim(),
        password: imapForm.password,
        imapUsername: imapForm.imapUsername.trim() || imapForm.email.trim(),
        imapHost: imapForm.imapHost.trim(),
        imapPort: Number(imapForm.imapPort) || 143,
        imapSecure: !!imapForm.imapSecure,
        smtpHost: imapForm.smtpHost.trim() || imapForm.imapHost.trim(),
        smtpPort: Number(imapForm.smtpPort) || 587,
        smtpSecure: !!imapForm.smtpSecure,
      });
      setOpenImap(false);
      setImapForm((p) => ({ ...p, password: "" }));
      await refreshAccountsAndNotifyParent();
      setImapNotice({
        open: true,
        title: "Cuenta IMAP guardada",
        message:
          "Usa Sincronizar en el menú de UNIBOX para traer los correos del servidor.",
        severity: "success",
      });
    } catch (e) {
      console.error(e);
      const st = e?.response?.status;
      if (st === 404) {
        setImapNotice({
          open: true,
          title: "No se encontró el servicio (404)",
          message:
            `El backend no expone POST /auth/imap/connect en:\n${imapUrl}\n\n` +
            `• REACT_APP_API_URL debe ser la URL del unibox-backend (suele ser puerto 4000), no la del propio React.\n` +
            `• Ejecuta npm run build en unibox-backend y reinicia npm start (o usa npm run dev).\n` +
            `• Comprueba en la terminal del backend que arranca sin errores.`,
          severity: "error",
        });
        return;
      }
      const d = e?.response?.data;
      const msg = [d?.error, d?.hint, d?.details].filter(Boolean).join("\n\n");
      setImapNotice({
        open: true,
        title: "No se pudo guardar la cuenta IMAP",
        message:
          msg ||
          e?.message ||
          "Revisa la consola de red y los logs del backend.",
        severity: "error",
      });
    } finally {
      setImapBusy(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ fontSize: 26, fontWeight: 800, mb: 2 }}>
        Gestión de cuentas de correo
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ backgroundColor: "#69cbf9", color: "#000" }}
          onClick={openGoogleOAuth}
        >
          Añadir cuenta Google
        </Button>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setOpenImap(true)}
        >
          Añadir IMAP / servidor propio
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <List sx={{ mt: 1 }}>
        {accounts.map((acc) => (
          <ListItem
            key={acc.id}
            sx={{
              mb: 1,
              borderRadius: "10px",
              border: "1px solid #e0e0e0",
              backgroundColor: "#fafafa",
            }}
            secondaryAction={
              <>
                <Tooltip title="Editar firmas">
                  <IconButton onClick={() => openEditDialog(acc)}  sx={{ border: "1px solid #96cea8", "&:hover": {
                      bgcolor: "#fee2e2",
                      borderColor: "#fecaca",
                      color: "#96cea8",
                    }, }}>
                    <EditIcon sx={{ color: "#96cea8" }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Eliminar cuenta">
                  <IconButton onClick={() => deleteAccount(acc.id)} sx={{ ml: 1,border: "1px solid #d9534f", "&:hover": {
                      bgcolor: "#fee2e2",
                      borderColor: "#fecaca",
                      color: "#b91c1c",
                    }, }}>
                    <DeleteIcon sx={{ color: "#d9534f",
                    
                    }} />
                  </IconButton>
                  
                </Tooltip>
              </>
            }
          >
            <ListItemAvatar>
              <Avatar
                sx={{
                  bgcolor: stringToColor(acc.email),
                  width: 40,
                  height: 40,
                  fontSize: 14,
                }}
              >
                {acc.email[0].toUpperCase()}
              </Avatar>
            </ListItemAvatar>

            <ListItemText
              primary={acc.email}
              secondary={`${
                acc.account_type === "imap" ? "IMAP" : "Google"
              } • ID: ${acc.id}${
                acc.account_type === "imap"
                  ? ""
                  : ` • Historial: ${
                      acc.last_history_id ? acc.last_history_id : "—"
                    }`
              }`}
            />
          </ListItem>
        ))}
      </List>

      <Dialog
        open={openImap}
        onClose={() => !imapBusy && setOpenImap(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Conectar buzón IMAP (ej. mail.dlx.dk)</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Typography sx={{ fontSize: 13, color: "#64748b" }}>
            Puerto 143 suele usar STARTTLS (deja «SSL directo» desmarcado). Si el envío falla, prueba SMTP 587 o 465 según tu proveedor.
          </Typography>
          <TextField
            label="Email"
            value={imapForm.email}
            onChange={(e) => setImapForm((p) => ({ ...p, email: e.target.value }))}
            fullWidth
            required
          />
          <TextField
            label="Contraseña"
            type="password"
            value={imapForm.password}
            onChange={(e) => setImapForm((p) => ({ ...p, password: e.target.value }))}
            fullWidth
            required
          />
          <TextField
            label="Usuario IMAP (si distinto del email)"
            value={imapForm.imapUsername}
            onChange={(e) =>
              setImapForm((p) => ({ ...p, imapUsername: e.target.value }))
            }
            fullWidth
          />
          <TextField
            label="Servidor IMAP"
            value={imapForm.imapHost}
            onChange={(e) => setImapForm((p) => ({ ...p, imapHost: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Puerto IMAP"
            value={imapForm.imapPort}
            onChange={(e) => setImapForm((p) => ({ ...p, imapPort: e.target.value }))}
            fullWidth
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={imapForm.imapSecure}
                onChange={(e) =>
                  setImapForm((p) => ({ ...p, imapSecure: e.target.checked }))
                }
              />
            }
            label="SSL/TLS directo (p. ej. puerto 993)"
          />
          <TextField
            label="Servidor SMTP"
            value={imapForm.smtpHost}
            onChange={(e) => setImapForm((p) => ({ ...p, smtpHost: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Puerto SMTP"
            value={imapForm.smtpPort}
            onChange={(e) => setImapForm((p) => ({ ...p, smtpPort: e.target.value }))}
            fullWidth
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={imapForm.smtpSecure}
                onChange={(e) =>
                  setImapForm((p) => ({ ...p, smtpSecure: e.target.checked }))
                }
              />
            }
            label="SMTP con SSL directo (p. ej. puerto 465)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImap(false)} disabled={imapBusy}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={submitImap}
            disabled={imapBusy}
            sx={{ backgroundColor: "#69cbf9", color: "#000" }}
          >
            {imapBusy ? "Guardando…" : "Guardar y conectar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={imapNotice.open}
        onClose={closeImapNotice}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
          {imapNotice.title}
        </DialogTitle>
        <DialogContent>
          <Alert
            severity={
              imapNotice.severity === "success"
                ? "success"
                : imapNotice.severity === "warning"
                  ? "warning"
                  : "error"
            }
            variant="outlined"
            sx={{
              alignItems: "flex-start",
              "& .MuiAlert-message": { width: "100%" },
            }}
          >
            <Typography
              component="div"
              variant="body2"
              sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
            >
              {imapNotice.message}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={closeImapNotice}
            sx={{ backgroundColor: "#69cbf9", color: "#0f172a" }}
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOGO DE EDICIÓN */}
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Editar firmas — {editingAccount?.email}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>
            Firma estándar
          </Typography>

          <Editor
            apiKey="3wlnbj1d6oyd7czfh9l7yh3yh9dnhvdh2t5ovk1m4dgdtaxl"
            value={signatureHtml}
            init={{
              height: 300,
              menubar: true,
              plugins:
                "advlist autolink lists link image charmap preview anchor " +
                "searchreplace visualblocks code fullscreen " +
                "insertdatetime media table paste help wordcount",
              toolbar:
                "undo redo | formatselect | bold italic underline | " +
                "alignleft aligncenter alignright alignjustify | " +
                "bullist numlist outdent indent | link image | removeformat",
              branding: false,
            }}
            onEditorChange={(content) => setSignatureHtml(content)}
          />

          <Button variant="outlined" component="label" sx={{ mb: 3 }}>
            Subir Logo
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => handleLogoUpload(e)}
            />
          </Button>
          {logoPath && (
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                Vista previa del logo
              </Typography>

              <img
                /* src={`http://localhost:4000${logoPath}`} */
                src={logoPath}
                alt="Firma Logo"
                style={{
                  maxWidth: "180px",
                  border: "1px solid #ddd",
                  padding: 4,
                  borderRadius: 6,
                  background: "#fff",
                }}
              />
            </Box>
          )}

          <Typography sx={{ fontWeight: 700, mb: 1 }}>
            Firma para respuestas
          </Typography>

          <Editor
            apiKey="3wlnbj1d6oyd7czfh9l7yh3yh9dnhvdh2t5ovk1m4dgdtaxl"
            value={replySignatureHtml}
            init={{
              height: 200,
              menubar: false,
              plugins: "link lists",
              toolbar: "bold italic underline | bullist numlist | link",
              branding: false,
            }}
            onEditorChange={(content) => setReplySignatureHtml(content)}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancelar</Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#69cbf9", color: "#000" }}
            onClick={saveSignatures}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
